import React, { useContext, useState } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import api from '../../utils/api'
import { CaseDetailContext } from './CaseDetailContext'
import InlineBrandLoader from '../shared/loaders/InlineBrandLoader'
import EditorToolbar from '../email-editor/EditorToolbar'
import { htmlToTiptap, tiptapToHtml } from '../email-editor/htmlConversion'
import { EMAIL_BODY_CSS } from '../email-editor/emailStyles'

/**
 * Disclosure — the neutral's arbitration disclosure, run by the case manager.
 *
 * Everything here is served by scale-be through cm-be's disclosure proxy, so the
 * document a neutral puts their name to is byte-identical to the one the
 * mediator portal produces. This screen only chooses recipients and sends.
 *
 * The three actions mirror the mediator side exactly:
 *   Download PDF  — streams the document, sends nothing. Safe to click.
 *   Send          — emails the ticked parties, CCs the case manager.
 *   Mark as sent  — records a disclosure sent outside the platform and opens
 *                   the monitoring window, without emailing anyone.
 */
export default function Disclosure({ onToast }) {
    const { caseData } = useContext(CaseDetailContext)

    const [open, setOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [busy, setBusy] = useState('')
    const [error, setError] = useState('')
    const [preview, setPreview] = useState(null)
    const [ticked, setTicked] = useState([])
    const [subject, setSubject] = useState('')

    const caseId = caseData?.id

    /* The cover letter is editable here for the same reason it is on the
     * mediator side: the neutral's own wording belongs to them, and the letter
     * is both the email body AND the PDF's first page. Same extension set as
     * the CM email-template editor, so the two behave identically. */
    const editor = useEditor({
        extensions: [
            StarterKit.configure({ heading: { levels: [1, 2, 3] }, hardBreak: false }),
            Underline,
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Link.configure({
                openOnClick: false,
                autolink: false,
                HTMLAttributes: { rel: 'noopener noreferrer' },
            }),
        ],
        content: '<p></p>',
    })

    // What the send and the PDF both carry. Falls back to the server's default
    // letter if the editor has not mounted yet.
    const coverLetterHtml = () =>
        editor ? tiptapToHtml(editor.getHTML()) : preview?.cover_letter_html || ''

    const load = async () => {
        if (!caseId) return
        setOpen(true)
        setLoading(true)
        setError('')
        try {
            const { data } = await api.get(`/cases/${caseId}/disclosure/preview`)
            setPreview(data)
            // Default to every party on the case, which is what a disclosure is for.
            setTicked((data?.recipients || []).map((r) => r.email))
            setSubject(data?.subject || 'Arbitration Disclosure')
            editor?.commands.setContent(
                htmlToTiptap(data?.cover_letter_html || data?.body_html || '<p></p>')
            )
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                'Could not load the disclosure for this case.'
            setError(msg)
        } finally {
            setLoading(false)
        }
    }

    const close = () => {
        if (busy) return
        setOpen(false)
        setPreview(null)
        setTicked([])
        setSubject('')
        setError('')
    }

    const toggle = (email) =>
        setTicked((prev) =>
            prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
        )

    const downloadPdf = async () => {
        setBusy('pdf')
        try {
            const res = await api.post(
                `/cases/${caseId}/disclosure/pdf`,
                { cover_letter_html: coverLetterHtml() },
                { responseType: 'blob' }
            )
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
            const a = document.createElement('a')
            a.href = url
            a.download = `Disclosure - ${caseData?.case_name || caseId}.pdf`
            document.body.appendChild(a)
            a.click()
            a.remove()
            window.URL.revokeObjectURL(url)
        } catch (err) {
            onToast?.({ type: 'error', message: 'Could not generate the PDF' })
        } finally {
            setBusy('')
        }
    }

    const send = async () => {
        if (!ticked.length) return
        setBusy('send')
        try {
            await api.post(`/cases/${caseId}/disclosure/send`, {
                subject,
                cover_letter_html: coverLetterHtml(),
                recipients: ticked,
            })
            onToast?.({
                type: 'success',
                message: `Disclosure sent to ${ticked.length} recipient${ticked.length === 1 ? '' : 's'}`,
            })
            close()
        } catch (err) {
            const msg = err?.response?.data?.message || 'Could not send the disclosure'
            onToast?.({ type: 'error', message: msg })
        } finally {
            setBusy('')
        }
    }

    const markSent = async () => {
        setBusy('mark')
        try {
            await api.post(`/cases/${caseId}/disclosure/mark-sent`, {})
            onToast?.({ type: 'success', message: 'Recorded as sent' })
            close()
        } catch (err) {
            onToast?.({ type: 'error', message: 'Could not record the disclosure' })
        } finally {
            setBusy('')
        }
    }

    const summary = preview?.summary || {}
    const firms = preview?.firms || []
    const recipients = preview?.recipients || []
    const sent = preview?.sent_state || {}

    return (
        <div className="w-full m-0 p-0 h-full flex items-stretch">
            <button
                type="button"
                onClick={load}
                className="bg-white border border-gray-300 text-gray-600 rounded-md h-11 px-4 py-2 flex justify-between gap-2 cursor-pointer items-center w-full hover:bg-gray-50 transition-colors"
            >
                <span className="text-sm font-medium min-w-0 truncate">Disclosure</span>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.6" stroke="currentColor" className="h-5 w-5 flex-shrink-0 text-gray-500">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                </svg>
            </button>

            {open && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4" onClick={close}>
                    <style>{EMAIL_BODY_CSS}</style>
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="border-b px-6 py-4 flex items-start justify-between gap-4">
                            <div className="min-w-0">
                                <h5 className="text-lg font-semibold">Neutral's Disclosure Statement</h5>
                                <p className="text-xs text-gray-500 mt-0.5 truncate">{caseData?.case_name}</p>
                            </div>
                            <button type="button" onClick={close} disabled={!!busy} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
                        </div>

                        <div className="px-6 py-5 overflow-y-auto">
                            {loading && (
                                <InlineBrandLoader label="Please wait…" sublabel="Building the disclosure" />
                            )}

                            {!loading && busy === 'pdf' && (
                                <InlineBrandLoader label="Please wait…" sublabel="Preparing the PDF" />
                            )}

                            {!loading && busy === 'send' && (
                                <InlineBrandLoader label="Please wait…" sublabel="Sending the disclosure" />
                            )}

                            {!loading && !busy && error && (
                                <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
                                    <p className="text-sm text-amber-900">{error}</p>
                                </div>
                            )}

                            {!loading && !busy && !error && preview && (
                                <>
                                    {sent.status && sent.status !== 'none' && (
                                        <div className="mb-4 rounded-lg bg-gray-50 border border-gray-200 px-4 py-2">
                                            <p className="text-xs text-gray-600">
                                                Already sent{sent.count ? ` · ${sent.count} time${sent.count === 1 ? '' : 's'}` : ''}
                                                {sent.at ? ` · last on ${new Date(sent.at).toLocaleDateString()}` : ''}
                                            </p>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-3 gap-3 mb-5">
                                        <Stat label="Firms" value={firms.length} />
                                        <Stat label="Matters listed" value={summary.listedMatterCount ?? 0} />
                                        <Stat label="Prior as neutral" value={summary.priorAsMediator ?? 0} />
                                    </div>

                                    {firms.length > 0 && (
                                        <div className="mb-5">
                                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Firms on this case</p>
                                            <div className="border border-gray-200 rounded-lg divide-y">
                                                {firms.map((f) => (
                                                    <div key={f.domain} className="px-4 py-2 flex items-center justify-between gap-3">
                                                        <span className="text-sm text-gray-800 truncate">{f.domain}</span>
                                                        <span className="text-xs text-gray-500 whitespace-nowrap">
                                                            {f.caseCount} matter{f.caseCount === 1 ? '' : 's'}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Subject</p>
                                    <input
                                        id="disclosure-subject"
                                        type="text"
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-5 focus:outline-none focus:ring-2 focus:ring-[#0F3D35]/30"
                                    />

                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Cover letter</p>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSubject(preview?.subject || 'Arbitration Disclosure')
                                                editor?.commands.setContent(
                                                    htmlToTiptap(preview?.cover_letter_html || preview?.body_html || '<p></p>')
                                                )
                                            }}
                                            className="text-xs text-gray-500 hover:text-gray-800 underline"
                                        >
                                            Reset to default
                                        </button>
                                    </div>
                                    <div className="border border-gray-300 rounded-lg overflow-hidden mb-1">
                                        <EditorToolbar editor={editor} />
                                        <div className="bg-white max-h-72 overflow-auto">
                                            <div className="email-body-content px-4 py-3">
                                                <LetterheadPreview letterhead={preview?.letterhead} />
                                                <EditorContent editor={editor} />
                                            </div>
                                        </div>
                                    </div>
                                    <p className="text-xs text-gray-500 mb-5">
                                        This is the email body and the first page of the PDF.
                                    </p>

                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Send to</p>
                                    <div className="border border-gray-200 rounded-lg divide-y">
                                        {recipients.map((r) => (
                                            <label key={r.email} className="px-4 py-2.5 flex items-center gap-3 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={ticked.includes(r.email)}
                                                    onChange={() => toggle(r.email)}
                                                    className="h-4 w-4 flex-shrink-0"
                                                />
                                                <span className="min-w-0 flex-1">
                                                    <span className="block text-sm text-gray-900 truncate">{r.name}</span>
                                                    <span className="block text-xs text-gray-500 truncate">{r.email}</span>
                                                </span>
                                                <span className="text-xs text-gray-500 whitespace-nowrap">{r.role_label}</span>
                                            </label>
                                        ))}
                                        {recipients.length === 0 && (
                                            <p className="px-4 py-3 text-sm text-gray-500">No counsel on this case to send to.</p>
                                        )}
                                    </div>

                                    {preview.case_manager_cc && (
                                        <p className="text-xs text-gray-500 mt-2">
                                            Copied to {preview.case_manager_cc}
                                        </p>
                                    )}
                                </>
                            )}
                        </div>

                        {!loading && !error && preview && (
                            <div className="border-t px-6 py-4 flex flex-wrap items-center justify-between gap-3">
                                <button
                                    type="button"
                                    onClick={markSent}
                                    disabled={!!busy}
                                    className="text-sm text-gray-600 hover:text-gray-900 underline disabled:opacity-60"
                                >
                                    {busy === 'mark' ? 'Recording…' : 'Mark as sent'}
                                </button>
                                <div className="flex gap-3 ml-auto">
                                    <button
                                        type="button"
                                        onClick={downloadPdf}
                                        disabled={!!busy}
                                        className="bg-[#EBEBEB] text-black font-semibold rounded-xl px-5 py-2.5 text-sm disabled:opacity-60"
                                    >
                                        {busy === 'pdf' ? 'Preparing…' : 'Download PDF'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={send}
                                        disabled={!!busy || ticked.length === 0}
                                        className="bg-[#0F3D35] text-white font-semibold rounded-xl px-5 py-2.5 text-sm disabled:opacity-60"
                                    >
                                        {busy === 'send' ? 'Sending…' : `Send${ticked.length ? ` (${ticked.length})` : ''}`}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

/**
 * The letterhead band of the PDF, read-only above the editable letter — the
 * same block the mediator portal shows, so the case manager sees the document
 * as the parties will receive it. The neutral's logo and address come from
 * their own account, so a neutral who has not set one falls back to their name.
 */
function LetterheadPreview({ letterhead }) {
    if (!letterhead) return null
    const lines = [
        ...(letterhead.address_lines || []),
        letterhead.phone ? `Phone: ${letterhead.phone}` : '',
    ].filter(Boolean)
    return (
        <div className="mb-5 flex items-end justify-between gap-4 border-b border-[#105F59] pb-3">
            <div className="min-w-0">
                {letterhead.logo_url ? (
                    <img src={letterhead.logo_url} alt="" className="max-h-14 max-w-[180px] object-contain" />
                ) : letterhead.mark === 'abc-company' ? (
                    <div className="flex items-center gap-2">
                        <svg width="38" height="38" viewBox="0 0 38 38" aria-hidden="true">
                            <rect width="38" height="38" rx="6" fill="#105F59" />
                            <circle cx="15" cy="19" r="8.5" fill="none" stroke="#fff" strokeWidth="2.1" />
                            <circle cx="23" cy="19" r="8.5" fill="none" stroke="#fff" strokeWidth="2.1" />
                        </svg>
                        <div className="leading-tight">
                            <div className="text-lg font-bold text-[#0F4F4A]">ABC Company</div>
                            <div className="text-[9px] tracking-[0.18em] text-[#105F59]">DISPUTE RESOLUTION</div>
                        </div>
                    </div>
                ) : (
                    <div className="text-lg font-bold text-[#105F59]">{letterhead.name}</div>
                )}
            </div>
            <div className="text-right text-[11px] leading-snug text-[#105F59]">
                {(letterhead.logo_url || letterhead.mark) && letterhead.name && (
                    <div className="text-[13px] font-bold">{letterhead.name}</div>
                )}
                {lines.map((l) => (
                    <div key={l}>{l}</div>
                ))}
                {letterhead.email && <div className="mt-1 text-[12px] font-bold">{letterhead.email}</div>}
            </div>
        </div>
    )
}

function Stat({ label, value }) {
    return (
        <div className="border border-gray-200 rounded-lg px-3 py-2">
            <p className="text-lg font-semibold text-gray-900 leading-tight">{value}</p>
            <p className="text-xs text-gray-500">{label}</p>
        </div>
    )
}
